import { Dices, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * The Minimum / Maximum / Random strip that resolves formula answers against a concrete scenario.
 *
 * One component, because numerical and slider had the same markup twice — hand-copied, and already
 * drifted: slider's was missing the `sticky` that numerical's own comment argues for ("long feedback
 * lists would otherwise scroll it out of view exactly when it's needed most"). Copying it a third
 * time was how that gap would recur.
 *
 * Deliberately stateless. The two blocks drive different roll state — numerical keeps a scenario
 * separate from its sticky-bar roll, slider drives its own block-test roll because the card IS its
 * test surface — and forcing one model on both would break whichever lost. This owns the control;
 * the caller owns what the control means.
 */
export function VariableTestController({
  mode, onSelect, onClear, extremaAvailable, calculation,
}: {
  mode: 'min' | 'max' | 'random' | null;
  onSelect: (mode: 'min' | 'max' | 'random') => void;
  /** Omit to hide the dismiss control — slider has no "no scenario" resting state to return to. */
  onClear?: () => void;
  /** False when the search could not run: a derived or missing variable, or a domain too large to
   * enumerate. Min/Max are then disabled. */
  extremaAvailable: boolean;
  /**
   * Whether to SAY why Min/Max are disabled, rather than just disabling them.
   *
   * The explanation is worth a line when Min/Max are the point — an answer that varies has a
   * smallest and a largest form, and a teacher who came to see them deserves to know why they
   * cannot. It is not worth one when the variables live only in FEEDBACK conditions: "Minimum"
   * there means the roll that pushes a targeted boundary lowest, which nobody arrives expecting, so
   * a sentence explaining its absence introduces the idea only to withdraw it. Random still works
   * and is the control that case actually wants.
   */
  /**
   * What the scenario resolves to — the caller's own `equation → value` line, drawn on its own row
   * beneath the controls.
   *
   * Optional because only numerical passes one so far, and because the component's contract is that
   * the caller owns what the control MEANS. It earns its place here rather than beside the answer:
   * with two variables, "Minimum" names one roll out of many and is unreadable until you can see
   * which values it picked.
   */
  calculation?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const segBtn = (active: boolean, enabled = true) =>
    `rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
      active ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
    } ${!enabled ? 'cursor-not-allowed opacity-40' : ''}`;

  return (
    // Solid (not translucent) background: this tray sits over scrolling content rather than inline,
    // so any translucency lets content show through as it scrolls past.
    //
    // WHERE it sits is the caller's, and there are two answers. Slider, compass and angle pass it
    // as the feedback panel's `beforeSlot`, pinned at the top of that scrollport — InputBlockFrame
    // stacks it in one sticky box with the shared toolbar, because two independent `top-0` stickies
    // in the same scrollport landed on the same coordinate and this read as unpinned. Numerical
    // passes it as `scenarioBarSlot` instead, above the answer bar and outside the scrollport, so
    // that one scenario spans both of its tabs.
    //
    // Controls on ONE row, spread edge to edge, with the equation centred beneath them. This was
    // three stacked rows — an uppercase heading, the controls, then the calculation — a lot of
    // chrome above an editor already fighting for height on a phone, to say one short thing. The
    // heading became an inline label beside what it labels, and the row it used to occupy is gone.
    //
    // `justify-between` rather than a huddle at the left edge: four controls bunched together in a
    // full-width tray read as an unfinished row, and spreading them gives each a target area big
    // enough to hit on a phone without making any of them physically bigger.
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border border-ws-border-strong bg-ws-in-progress-bg px-4 py-1.5'>
      <div className='flex w-full items-center justify-between gap-2'>
        <span className='text-xs font-semibold leading-none text-ws-primary'>
          {t('blocks.variableTesting.setLabel')}
        </span>
        <div role='group' aria-label={t('blocks.variableTesting.label')}
          className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
          <button type='button' disabled={!extremaAvailable} aria-pressed={mode === 'min'}
            onClick={() => onSelect('min')} className={segBtn(mode === 'min', extremaAvailable)}>
            {t('blocks.variableTesting.min')}
          </button>
          <button type='button' disabled={!extremaAvailable} aria-pressed={mode === 'max'}
            onClick={() => onSelect('max')} className={segBtn(mode === 'max', extremaAvailable)}>
            {t('blocks.variableTesting.max')}
          </button>
        </div>
        {/* A repeatable action, not a third exclusive state — every click re-rolls — so it sits
            outside the segmented control, where it would otherwise read as "select once". */}
        <button type='button' onClick={() => onSelect('random')} aria-pressed={mode === 'random'}
          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
            mode === 'random' ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-primary/10 hover:text-ws-primary'
          }`}>
          <Dices aria-hidden='true' className='h-3.5 w-3.5' />
          {t('blocks.variableTesting.random')}
        </button>
        {/* HIDDEN, not removed, once the row spreads edge to edge: with `justify-between` a control
            that comes and goes re-spaces everything beside it, so picking a scenario would shunt
            Random leftward at the moment of the click. The slot itself only exists for callers that
            HAVE a resting state to return to — slider has none, and a permanently blank gap at its
            right edge would be reserving space for a button that is never coming. */}
        {onClear && (
          <button type='button' onClick={onClear} aria-label={t('blocks.variableTesting.clear')}
            aria-hidden={!mode} tabIndex={mode ? undefined : -1} disabled={!mode}
            className={`rounded-md p-1 text-ws-muted transition-colors hover:bg-black/10 hover:text-ws-text dark:hover:bg-white/10 ${
              mode ? '' : 'invisible'}`}>
            <X aria-hidden='true' className='h-3.5 w-3.5' />
          </button>
        )}
      </div>
      {/* `basis-full` is what lets the equation CENTRE: a content-sized flex item has no width to
          centre anything within, so it would sit wherever the wrap dropped it. Full-basis also puts
          it on its own row under a control row that already spans the width. */}
      {calculation && <div className='basis-full'>{calculation}</div>}
    </div>
  );
}
